import { useState, useEffect, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { ArrowLeft, Send, Check, CheckCheck } from "lucide-react";
import { toast } from "sonner";

export default function ConversationPage() {
  const { id } = useParams();
  const { api, user } = useAuth();
  const navigate = useNavigate();
  const [messages, setMessages] = useState([]);
  const [otherUser, setOtherUser] = useState(null);
  const [newMsg, setNewMsg] = useState("");
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(true);
  const bottomRef = useRef(null);
  const pollRef = useRef(null);

  const fetchMessages = useCallback(async () => {
    try {
      const res = await api.get(`/messages/conversations/${id}`, { params: { limit: 100 } });
      setMessages(res.data.messages);
      setOtherUser(res.data.other_user);
      return res.data.messages;
    } catch {
      navigate("/messages");
    }
  }, [api, id, navigate]);

  useEffect(() => {
    setLoading(true);
    fetchMessages().finally(() => setLoading(false));

    // Poll for new messages
    pollRef.current = setInterval(fetchMessages, 5000);
    return () => clearInterval(pollRef.current);
  }, [fetchMessages]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const handleSend = async () => {
    if (!newMsg.trim() || sending) return;
    setSending(true);
    try {
      const res = await api.post(`/messages/conversations/${id}`, { content: newMsg });
      setMessages((prev) => [...prev, res.data.message]);
      setNewMsg("");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    }
    setSending(false);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const timeFormat = (dateStr) => {
    try {
      return new Date(dateStr).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" });
    } catch {
      return "";
    }
  };

  const dateLabel = (dateStr) => {
    try {
      return new Date(dateStr).toLocaleDateString("fr-FR", { weekday: "short", day: "numeric", month: "short" });
    } catch {
      return "";
    }
  };

  // Group messages by date
  const groupedMessages = [];
  let lastDate = null;
  for (const msg of messages) {
    const d = dateLabel(msg.created_at);
    if (d !== lastDate) {
      groupedMessages.push({ type: "date", label: d });
      lastDate = d;
    }
    groupedMessages.push({ type: "msg", ...msg });
  }

  return (
    <div className="flex flex-col h-[calc(100vh-140px)]" data-testid="conversation-page">
      {/* Header */}
      <div className="flex items-center gap-3 pb-4 border-b border-border">
        <button
          onClick={() => navigate("/messages")}
          className="p-1.5 rounded-xl hover:bg-muted text-muted-foreground transition-colors"
          data-testid="convo-back-btn"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div className="w-9 h-9 rounded-full bg-gradient-to-br from-[#FF6B35] to-[#FF8FA3] flex items-center justify-center shrink-0">
          {otherUser?.avatar_url ? (
            <img src={otherUser.avatar_url} alt="" className="w-full h-full rounded-full object-cover" />
          ) : (
            <span className="text-white font-heading font-bold text-sm">
              {(otherUser?.pseudo || "?")[0].toUpperCase()}
            </span>
          )}
        </div>
        <div>
          <p className="font-heading font-semibold text-sm text-foreground">{otherUser?.pseudo || "..."}</p>
          <p className="text-[10px] text-muted-foreground">{otherUser?.role === "artist" ? "Artiste" : "Fan"}</p>
        </div>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto py-4 space-y-1 scrollbar-hide">
        {loading ? (
          <div className="flex items-center justify-center h-full">
            <p className="text-sm text-muted-foreground">Chargement...</p>
          </div>
        ) : messages.length === 0 ? (
          <div className="flex items-center justify-center h-full">
            <p className="text-sm text-muted-foreground">Envoie le premier message !</p>
          </div>
        ) : (
          groupedMessages.map((item, i) => {
            if (item.type === "date") {
              return (
                <div key={`date-${i}`} className="flex justify-center py-2">
                  <span className="text-[10px] text-muted-foreground bg-card border border-border px-3 py-0.5 rounded-full">{item.label}</span>
                </div>
              );
            }
            const isMe = item.sender_id === user?.id;
            const readAt = item.read_at ? new Date(item.read_at) : null;
            const readLabel = readAt ? `Lu ${readAt.toLocaleDateString("fr-FR", { day: "numeric", month: "short" })} ${readAt.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })}` : null;
            return (
              <div
                key={item.id}
                className={`flex ${isMe ? "justify-end" : "justify-start"}`}
                data-testid={`message-${item.id}`}
              >
                <div className={`max-w-[75%] ${isMe ? "text-right" : "text-left"}`}>
                  <div
                    className={`px-4 py-2 rounded-2xl text-sm leading-relaxed inline-block text-left ${
                      isMe
                        ? "bg-[#FF6B35] text-white rounded-br-md"
                        : "bg-card border border-border text-card-foreground rounded-bl-md"
                    }`}
                  >
                    <p className="whitespace-pre-wrap break-words">{item.content}</p>
                    <div className={`flex items-center gap-1 mt-0.5 ${isMe ? "justify-end" : ""}`}>
                      <span className={`text-[10px] ${isMe ? "text-white/60" : "text-muted-foreground"}`}>
                        {timeFormat(item.created_at)}
                      </span>
                      {isMe && (
                        item.read ? (
                          <CheckCheck className="w-3.5 h-3.5 text-white/80" data-testid={`read-check-${item.id}`} />
                        ) : (
                          <Check className="w-3 h-3 text-white/50" data-testid={`sent-check-${item.id}`} />
                        )
                      )}
                    </div>
                  </div>
                  {isMe && readLabel && (
                    <p className="text-[9px] text-muted-foreground mt-0.5 pr-1" data-testid={`read-at-${item.id}`}>{readLabel}</p>
                  )}
                </div>
              </div>
            );
          })
        )}
        <div ref={bottomRef} />
      </div>

      {/* Input */}
      <div className="pt-3 border-t border-border">
        <div className="flex gap-2">
          <textarea
            placeholder="Ecris un message..."
            value={newMsg}
            onChange={(e) => setNewMsg(e.target.value)}
            onKeyDown={handleKeyDown}
            rows={1}
            className="flex-1 bg-card border border-border rounded-2xl px-4 py-2.5 text-sm text-card-foreground placeholder:text-muted-foreground focus:border-[#FF6B35] focus:ring-1 focus:ring-[#FF6B35] outline-none resize-none"
            data-testid="message-input"
          />
          <button
            onClick={handleSend}
            disabled={sending || !newMsg.trim()}
            className="bg-[#FF6B35] text-white p-2.5 rounded-2xl hover:scale-105 active:scale-95 transition-transform disabled:opacity-50 shrink-0"
            data-testid="message-send-btn"
          >
            <Send className="w-5 h-5" />
          </button>
        </div>
      </div>
    </div>
  );
}
